import { ChevronRightIcon } from "lucide-react";
import { useState, type ReactElement } from "react";

import { Button } from "../ui/button";
import { StepRow } from "./FirstAccountSetupParts";
import { FirstAccountSetupStepPanel } from "./FirstAccountSetupStepPanel";
import type { FirstAccountSetupProps } from "./FirstAccountSetupTypes";
import {
  deriveFirstAccountSetupView,
  stepActionLabel,
  stepDetail,
  SETUP_STEPS,
  type UsageSource,
} from "./FirstAccountSetup.logic";
import type { ProviderSetupRow } from "./firstAccountSetupData";

export function FirstAccountSetup(props: FirstAccountSetupProps): ReactElement {
  const initialStepIndex = Math.max(
    0,
    SETUP_STEPS.findIndex((step) => step.id === props.initialStepId),
  );
  const [activeStepIndex, setActiveStepIndex] = useState(initialStepIndex);
  const [usageSource, setUsageSource] = useState<UsageSource>(
    props.initialUsageSource ?? "workspace",
  );
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const view = deriveFirstAccountSetupView(props, { activeStepIndex, selectedProjectId });

  function handleBack(): void {
    if (!view.canGoBack) return;
    const nextIndex = activeStepIndex - 1;
    setActiveStepIndex(nextIndex);
    props.onStepChange?.(SETUP_STEPS[nextIndex]?.id ?? "usage");
  }

  function handleContinue(): void {
    if (view.isFinalStep) {
      props.onComplete({ usageSource, selectedProjectId: view.effectiveSelectedProjectId });
      return;
    }
    const nextIndex = activeStepIndex + 1;
    setActiveStepIndex(nextIndex);
    props.onStepChange?.(SETUP_STEPS[nextIndex]?.id ?? "usage");
  }

  function handleUsageSourceChange(source: UsageSource): void {
    setUsageSource(source);
    props.onUsageSourceChange?.(source);
  }

  function handleProviderAction(providerId: ProviderSetupRow["id"]): void {
    props.onProviderConnect?.(providerId);
  }

  return (
    <main className="flex h-full min-h-0 w-full overflow-hidden bg-background text-foreground">
      <section
        className="grid h-full min-h-0 w-full grid-rows-[1fr_auto] overflow-hidden bg-background"
        aria-label="First account setup"
      >
        <div className="grid min-h-0 grid-cols-1 grid-rows-1 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <aside className="hidden min-h-0 flex-col p-4 lg:flex lg:overflow-y-auto lg:border-r lg:border-border">
            <div className="flex min-h-8 items-center">
              <p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
                Setup
              </p>
            </div>
            <ol className="mt-7 flex flex-col gap-1.5">
              {SETUP_STEPS.map((step, index) => (
                <StepRow
                  key={step.id}
                  activeIndex={activeStepIndex}
                  index={index}
                  step={{
                    ...step,
                    detail: stepDetail({
                      stepId: step.id,
                      defaultDetail: step.detail,
                      providerRows: view.providerRows,
                      projects: props.projects,
                    }),
                  }}
                />
              ))}
            </ol>
          </aside>

          <section className="min-h-0 overflow-y-auto">
            <div className="flex min-h-full min-w-0 items-start justify-center p-6 sm:p-8">
              <div className="w-full max-w-3xl">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
                      {view.activeContent.eyebrow}
                    </p>
                    <h2 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">
                      {view.activeContent.title}
                    </h2>
                    {view.activeContent.description ? (
                      <p className="mt-2 max-w-2xl text-sm leading-5 text-muted-foreground">
                        {view.activeContent.description}
                      </p>
                    ) : null}
                  </div>
                </div>

                <FirstAccountSetupStepPanel
                  activeStepId={view.activeStep.id}
                  providerRows={view.providerRows}
                  projects={props.projects}
                  projectWorkspaceOptions={props.projectWorkspaceOptions ?? []}
                  projectsLoading={props.projectsLoading ?? false}
                  selectedProjectId={view.effectiveSelectedProjectId}
                  selectedProviderLabel={view.selectedProviderLabel}
                  theme={view.theme}
                  uiFontSize={view.uiFontSize}
                  codeFontSize={view.codeFontSize}
                  sidebarLayoutMode={view.sidebarLayoutMode}
                  githubCapability={props.githubCapability ?? null}
                  githubCapabilityLoading={props.githubCapabilityLoading ?? false}
                  githubActionBusy={props.githubActionBusy ?? false}
                  githubAuthorizeUrl={props.githubAuthorizeUrl ?? null}
                  githubAuthorizeUrlLoading={props.githubAuthorizeUrlLoading ?? false}
                  pluginsLoading={props.pluginsLoading ?? false}
                  pluginsError={props.pluginsError ?? null}
                  pluginConnectionAttempt={props.pluginConnectionAttempt ?? null}
                  pluginConnectionAttempts={props.pluginConnectionAttempts ?? {}}
                  providerActionLabel={props.providerActionLabel ?? null}
                  providerActionBusy={props.providerActionBusy ?? false}
                  providerActionProviderId={props.providerActionProviderId ?? null}
                  providerCodePrompt={props.providerCodePrompt ?? false}
                  providerAuthorizeUrl={props.providerAuthorizeUrl ?? null}
                  providerNeedsRuntime={props.providerNeedsRuntime ?? false}
                  codexCloudSignInSlot={props.codexCloudSignInSlot ?? null}
                  claudeCloudSignInSlot={props.claudeCloudSignInSlot ?? null}
                  onGitHubConnect={() => props.onGitHubConnect?.()}
                  onPluginConnect={(pluginId) => props.onPluginConnect?.(pluginId)}
                  onOpenProjectImporter={props.onOpenProjectImporter}
                  onProjectChange={setSelectedProjectId}
                  onProviderAction={handleProviderAction}
                  onSubmitProviderCode={(code) => props.onSubmitProviderCode?.(code)}
                  onThemeChange={(nextTheme) => props.onThemeChange?.(nextTheme)}
                  onUiFontSizeChange={(fontSize) => props.onUiFontSizeChange?.(fontSize)}
                  onCodeFontSizeChange={(fontSize) => props.onCodeFontSizeChange?.(fontSize)}
                  onSidebarLayoutModeChange={(mode) => props.onSidebarLayoutModeChange?.(mode)}
                  onUsageSourceChange={handleUsageSourceChange}
                  environmentPhase={props.environmentPhase ?? "idle"}
                  {...(props.environmentId === undefined
                    ? {}
                    : { environmentId: props.environmentId })}
                  {...(props.environmentError === undefined
                    ? {}
                    : { environmentError: props.environmentError })}
                  {...(props.environmentWorkspaceLabel === undefined
                    ? {}
                    : { environmentWorkspaceLabel: props.environmentWorkspaceLabel })}
                  environmentWorkspaceOptions={props.environmentWorkspaceOptions ?? []}
                  onSelectEnvironmentWorkspace={(organizationId) =>
                    props.onSelectEnvironmentWorkspace?.(organizationId)
                  }
                  onEnsureEnvironment={() => props.onEnsureEnvironment?.()}
                  {...(props.plugins === undefined ? {} : { plugins: props.plugins })}
                />
              </div>
            </div>
          </section>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border px-4 py-3 sm:px-5">
          <Button
            className="min-h-11"
            disabled={!view.canGoBack}
            size="default"
            variant="outline"
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            className="min-h-11"
            size="default"
            disabled={view.environmentBlocksContinue}
            onClick={handleContinue}
          >
            {stepActionLabel(view.activeStep.id)}
            <ChevronRightIcon className="size-4" />
          </Button>
        </footer>
      </section>
    </main>
  );
}
